import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/styles';
import Select from 'react-select';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import { isEmptyArray } from 'formik';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { InputAdornment, TextField } from '@material-ui/core';
import { Search } from '@material-ui/icons';

import styles, {
  customTagSelectStyles,
  customStyles
} from '../styles/vacations';
import { translate } from '../../../i18n/I18nProvider';
import { reactSelectDefaultStyles } from '../../../consts/reactSelectDefaultStyles';
import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import Optional from '../../../utils/optional';
import vacationsColumns from './vacationsColumns';
import { mapeSelectedText, mapOptions } from '../actions/mapActions';
import { isStringEmpty } from '../../../utils/isDefined';

const Vacations = ({
  classes,
  arrayHelpers: { push },
  options,
  setFieldValue,
  tagsOptions
}) => {
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { localization } = useTableLocalization();

  useEffect(() => {
    const mappedOptions = options.map(option => {
      return { ...option, selected: false };
    });
    setSelected(mappedOptions);
  }, [options]);

  const onChange = selectedOptions => setTags(selectedOptions);

  const availableOptions = useMemo(
    () => {
      return [
        ...mapOptions({ options, tags, searchTerm }).filter(
          el => el.haveTags && el.foudedInSearch
        )
      ];
    },
    // eslint-disable-next-line
    [options, tags, searchTerm]
  );

  const tableOptions = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      search: false,
      columnsButton: false,
      showTitle: false,
      paging: false,
      maxBodyHeight: '300px',
      minBodyHeight: '300px',
      rowStyle: rowData => ({
        backgroundColor: Optional(selected.find(el => el.id === rowData.id))
          .map(s => (s.selected ? '#F3F3F3EE' : '#FFF'))
          .or('#FFF'),
        fontSize: '13px'
      }),
      headerStyle: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        fontSize: '13px'
      }
    };
  }, [selected]);

  const onSelectionChange = useCallback(
    (e, id) => {
      const mappedSelected = selected.map(el => {
        if (el.id !== id) {
          return el;
        }

        return { ...el, selected: e.target.checked };
      });

      setFieldValue('vacations', []);

      const dataToPush = mappedSelected.filter(el => el.selected === true);

      dataToPush.forEach(el => push(el));

      setSelected(mappedSelected);
    },
    [selected, setSelected, setFieldValue, push]
  );

  const onAllSelectionChange = useCallback(
    e => {
      if (isEmptyArray(tags) && isStringEmpty(searchTerm.trim())) {
        const mappedSelected = selected.map(el => {
          return { ...el, selected: e.target.checked };
        });

        setFieldValue('vacations', []);

        mappedSelected.forEach(el => push(el));

        setSelected(mappedSelected);
      } else {
        const availableOptionsIds = availableOptions.map(option => option.id);

        const mappedSelected = selected.map(el => {
          const shouldUpdate =
            availableOptionsIds.findIndex(optionId => optionId === el.id) !==
            -1;

          if (!shouldUpdate) {
            return el;
          }

          return { ...el, selected: e.target.checked };
        });

        setFieldValue('vacations', []);

        const dataToPush = mappedSelected.filter(el => el.selected === true);

        dataToPush.forEach(el => push(el));

        setSelected(mappedSelected);
      }
    },
    [
      selected,
      setSelected,
      setFieldValue,
      push,
      tags,
      availableOptions,
      searchTerm
    ]
  );

  const columns = useMemo(
    () =>
      vacationsColumns({
        selected,
        availableOptions,
        onSelectionChange,
        onAllSelectionChange
      }),

    [selected, onSelectionChange, onAllSelectionChange, availableOptions]
  );

  const handleSearchChange = useCallback(
    event => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  return (
    <div>
      <div className={classes.tagsContainer}>
        <Select
          styles={reactSelectDefaultStyles}
          customStyles={customTagSelectStyles}
          isSearchable
          options={tagsOptions}
          value={tags}
          onChange={onChange}
          placeholder={translate('SCHEDULES.SELECT_CATEGORY')}
          isMulti
          isRtl
        />
      </div>

      <div className={classes.searchContainer}>
        <TextField
          variant="outlined"
          margin="dense"
          fullWidth
          onChange={e => handleSearchChange(e)}
          value={searchTerm}
          style={customStyles.textfieldRoot}
          className={classes.searchField}
          placeholder={translate('TABLE.SEARCH')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm.length > 0 && (
                  <i
                    className={clsx('fas fa-times', classes.closeIcon)}
                    onClick={() =>
                      handleSearchChange({ target: { value: '' } })
                    }
                  ></i>
                )}
              </InputAdornment>
            )
          }}
          inputProps={{ autoComplete: 'off', 'data-lpignore': true }}
        />
      </div>

      <MaterialTable
        columns={columns}
        data={availableOptions}
        options={tableOptions}
        localization={localization}
        components={{
          Toolbar: props => (
            <MTableToolbar
              classes={{
                root: classes.toolbarRoot
              }}
              {...props}
            />
          )
        }}
      />
      <div className={classes.line}></div>
      <div>{mapeSelectedText({ selected, availableOptions })}</div>
    </div>
  );
};
const mapStatesToProps = ({ addingSchedules }) => {
  return {
    tagsOptions: addingSchedules.tags,
    options: addingSchedules.vacations
  };
};

export default flowRight(
  connect(mapStatesToProps),
  withStyles(styles)
)(Vacations);
